import {
  Component,
  forwardRef,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import {
  AgpFormUtil,
  CvaFormGroupComponent,
  ResetOptions
} from '@liberty-financial/agp-shared';

import { ContactSummary } from '@features/contacts/models';
import { Shareholder } from '@features/contacts/models/company';
import { formArrayAggregatedValidation } from '@shared/validators';

interface ShareholderForm {
  contact: ContactSummary;
  ownership: number;
  new: boolean;
  deleted: boolean;
}

@Component({
  selector: 'spark-contacts-company-shareholders',
  templateUrl: './shareholders.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CompanyShareholdersComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CompanyShareholdersComponent),
      multi: true
    }
  ]
})
export class CompanyShareholdersComponent
  extends CvaFormGroupComponent<Shareholder.Model[], ShareholderForm[]>
  implements OnChanges
{
  @Input() contacts: ContactSummary[] = [];
  @Input() isRequired = false;

  expandedIndex = -1;
  form = new FormArray<
    FormGroup<{
      contact: FormControl<ContactSummary | null>;
      ownership: FormControl<number | null>;
      new: FormControl<boolean>;
      deleted: FormControl<boolean>;
    }>
  >([], [formArrayAggregatedValidation('ownership')]);
  formGroup = new FormGroup({
    shareholders: this.form
  });

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isRequired) {
      AgpFormUtil.Validators.updateRequiredIfNecessary(this.form, changes.isRequired.currentValue);
    }
  }

  protected reset(opts?: ResetOptions | undefined): void {
    this.form?.reset(undefined, opts);
  }

  protected transformToModel = (
    values: ShareholderForm[]
  ): Shareholder.Model[] => {
    return values.map((d) => ({
      created: d.new,
      deleted: d.deleted,
      id: d.contact?.id,
      value: d.ownership
    }));
  };

  protected transformToForm = (v: Shareholder.Model[]): ShareholderForm[] => {
    const result: ShareholderForm[] = [];
    v.forEach((s) => {
      const contact = this.contacts.find((c) => c.id === s.id);
      if (contact) {
        this.addShareholder(false);
        result.push({
          contact,
          ownership: s.value || 0,
          new: false,
          deleted: false
        });
      }
    });
    return result;
  };

  addShareholder(newItem: boolean = true): void {
    this.form.push(
      new FormGroup({
        contact: new FormControl<ContactSummary | null>(null),
        ownership: new FormControl<number | null>(null),
        new: new FormControl(newItem, { nonNullable: true }),
        deleted: new FormControl<boolean>(false, { nonNullable: true })
      })
    );
    this.expandedIndex = this.form.length - 1;
  }

  removeShareholder(i: number): void {
    this.form.removeAt(i);
  }
}
